/* eslint-disable @typescript-eslint/no-use-before-define */
import { useDebounceFn, useLatest, useSize, useUpdateEffect } from 'ahooks';
import classNames from 'classnames';
import { cloneDeep, Dictionary, groupBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import type { SelectProps } from 'antd';
import { LiteralUnion } from 'antd/es/_util/type';
import { Input, Scrollbars, Select } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import { useLocaleStore } from '@/i18n';
import { useConfig } from '../configProvider';
import VipIcon from '../share/shareModal/vipIcon';
import { checkTeamStatusIsFree, useTeamInfoState } from '../teamInfo';
import { checkFontIdIsTeamFontId } from '../teamInfo/teamFontBiz';
import { FontsCategoriesRaw, searchFonts } from './apis';
import FontGroupItem from './components/fontGroupItem';
import FontItemCom from './components/fontItem';
import './index.less';

export interface FontItem {
    /** 字体素材iD */
    id?: number;
    /** 字体名称 */
    font_name: string;
    /** 是否vip */
    is_vip: LiteralUnion<'1' | '0', string>;
    material_id?: number;
    name: string;
    thumbnail: string;
    thumbnail_text_dark: string;
    thumbnail_text_white: string;
    /** 会员角标 */
    corner_mark: string;
    type_id: number;
    zip_url: string;
    font_file: string;
    font_group_name: string;
    font_weight_value: number;
    font_group_thumbnail_dark: string;
    font_group_thumbnail_white: string;
}

const { OptGroup } = Select;

const MIN_DROP_COMPONENT_HEIGHT = 300;

export type SearchFontDataFn<F extends FontItem = FontItem> = (arg: {
    keyword: string;
    /** 对应的文本内容, 用于字体列表的语言的排序 */
    text?: string;
    allFontData: RenderData<F>;
}) => RenderData<F> | Promise<RenderData<F>>;

export interface ITextFontFamilyProps<F extends FontItem = FontItem> {
    allFontData: Dictionary<F[]>;
    /** 对应的文本内容, 用于字体列表的语言的排序 */
    text?: string;
    value?: string;
    /** 对应的textLayerId或tableLayerId */
    id?: string;
    isVip?: boolean;
    /** 支持传入函数, 开启选项时再调用, 以此提高性能 */
    /**
     * 是否免费使用 vip 字体
     * @default false
     */
    freeUseVipFont?: boolean;
    usingFontList?: F[] | (() => F[]);
    onChange?: (data: F, value: string, isFromSearch: boolean) => void;
    /** 点击「开通会员，可免费商用该字体」按钮 */
    onClickVipBtn?: (entrance?: string) => void;
    onTrackShow?: (item: F) => void;
    popupClassName?: string;
    /** 默认开启搜索 */
    searchEnable?: boolean;
    /** 默认为本地搜索 */
    searchFontData?: SearchFontDataFn<F>;
    /** 默认为0 */
    searchDebounceDelay?: number;
    customHeightInfo?: { dropListHeight: string; dropComponentHeight: string };
    onDropdownVisibleChange?: SelectProps['onDropdownVisibleChange'];
}

export type RenderData<F extends FontItem = FontItem> = Dictionary<F[]>;

/** 分组数据 */
export type RenderGroupData<F extends FontItem = FontItem> = Dictionary<Dictionary<F[]>>;

const defaultSearchFontData = <F extends FontItem = FontItem>({
    keyword,
    allFontData,
}: Parameters<SearchFontDataFn<F>>[0]): ReturnType<SearchFontDataFn<F>> => {
    if (!keyword) {
        return allFontData;
    }
    let result: RenderData<F> = {};
    Object.keys(allFontData).forEach((v) => {
        allFontData[v].forEach((item: F) => {
            if (
                item.name
                    ?.replace(/\s/g, '')
                    ?.toLocaleLowerCase()
                    ?.indexOf(keyword.replace(/\s/g, '').toLocaleLowerCase()) !== -1
            ) {
                if (result[v]) {
                    result[v].push(item);
                } else {
                    result[v] = [];
                    result[v].push(item);
                }
            }
        });
    });
    return result;
};

const TextFontFamily = <F extends FontItem = FontItem>({
    allFontData,
    value,
    id = '',
    text = '',
    isVip,
    freeUseVipFont = false,
    usingFontList: usingFontListProp = [],
    onChange,
    onClickVipBtn,
    onTrackShow,
    popupClassName = '',
    searchEnable = true,
    searchFontData = defaultSearchFontData,
    searchDebounceDelay = 0,
    customHeightInfo,
    onDropdownVisibleChange,
}: ITextFontFamilyProps<F>) => {
    const { $t } = useLocaleStore();
    const { theme } = useConfig();
    const teamInfo = useTeamInfoState();

    const [open, setOpen] = useState(false);
    const [fontName, setFontName] = useState<string | null>(null);
    const [vipBtnShow, setVipBtnShow] = useState(false);
    const [fontSearchText, setFontSearchText] = useState('');
    const [renderType, setRenderType] = useState<'all' | 'search'>('all');
    const [isEmptyShow, setIsEmptyShow] = useState(false);
    const [searchedData, setSearchedData] = useState<RenderData<F> | null>(null);
    const [loading, setLoading] = useState(false);

    const usingFontListPropRef = useLatest(usingFontListProp);
    const [usingFontList, setUsingFontList] = useState<F[]>([]);
    const [selectDom, setSelectDom] = useState<HTMLDivElement | null>(null);
    const { rect: selectDomRect, refreshRect: refreshSelectDomRect } = useDomRect(selectDom);
    const selectDomRectBottom = selectDomRect?.bottom;
    const windowHeight = useSize(document.documentElement)?.height ?? 0;
    const [lazyLoadRenderKey, setLazyLoadRenderKey] = useState(0);

    /** 当前展开的字体组 */
    const [openGroups, setOpenGroups] = useState<string[]>([]);

    /**
     * 高度逻辑:
     *  1. 默认撑到最高, 直到撑到窗口边缘
     *  2. 搜索数量少的时候, 由列表撑开高度
     */
    const heightInfo = useMemo<{
        dropListHeight: number;
        dropComponentHeight: number;
    }>(() => {
        if (!selectDom) return { dropListHeight: 0, dropComponentHeight: 0 };

        // 下拉容器距离选择组件的距离
        const DROP_TOP = 4;
        // 下拉容器底部的内边距
        const PADDING_BOTTOM = 8;
        // 下拉容器距离页面底部的距离
        const MARGIN_BOTTOM = 16;
        const SEARCH_COMPONENT_HEIGHT = searchEnable ? 64 : 0;
        const expectedDropComponentHeight = windowHeight - (selectDomRectBottom ?? 0) - DROP_TOP - MARGIN_BOTTOM;
        const dropComponentHeight = Math.max(expectedDropComponentHeight, MIN_DROP_COMPONENT_HEIGHT);
        const dropListHeight = dropComponentHeight - PADDING_BOTTOM - SEARCH_COMPONENT_HEIGHT;

        if (renderType === 'search') {
            const categaryCount = Object.keys(searchedData ?? {}).length;
            const itemCount = Object.values(searchedData ?? {}).flat().length;
            const SCROLL_BAR_PADDING_TOP = 0;
            const SCROLL_BAR_PADDING_BOTTOM = 30;
            const CATEGARY_HEIGHT = 48;
            const ITEM_HEIGHT = 32;
            const expectDropListHeight =
                SCROLL_BAR_PADDING_TOP +
                SCROLL_BAR_PADDING_BOTTOM +
                (categaryCount * CATEGARY_HEIGHT + (categaryCount > 0 ? -16 : 0)) +
                itemCount * ITEM_HEIGHT;
            const resultDropListHeight = Math.max(
                Math.min(expectDropListHeight, dropListHeight),
                MIN_DROP_COMPONENT_HEIGHT - PADDING_BOTTOM,
            );
            const resultDropComponentHeight = resultDropListHeight + PADDING_BOTTOM + SEARCH_COMPONENT_HEIGHT;
            return {
                dropListHeight: resultDropListHeight,
                dropComponentHeight: resultDropComponentHeight,
            };
        }

        return {
            dropListHeight,
            dropComponentHeight,
        };
    }, [selectDomRectBottom, windowHeight, renderType, searchedData]);

    // reset
    useEffect(() => {
        setFontSearchText('');
        setRenderType('all');
        setIsEmptyShow(false);
        setSearchedData(null);

        if (!open) {
            setUsingFontList([]);
            return;
        }

        const list =
            typeof usingFontListPropRef.current === 'function'
                ? usingFontListPropRef.current()
                : usingFontListPropRef.current;
        setUsingFontList(list);
    }, [open, value, id]);

    useEffect(() => {
        if (value) {
            setFontName(value);
        }
    }, [value]);

    const groupFontsByLanguageAndFamily = () => {
        const result: RenderGroupData<F> = {};
        Object.keys(allFontData).forEach((v) => {
            // 有 font_group_name 的排前面
            allFontData[v].sort((a, b) => (b.font_group_name ? 1 : 0) - (a.font_group_name ? 1 : 0));

            const res = groupBy(allFontData[v], (item) => item.font_group_name || 'other');
            result[v] = res;
        });
        return result;
    };

    const resultRenderData: RenderData<F> | RenderGroupData<F> =
        renderType === 'all' ? groupFontsByLanguageAndFamily() : (searchedData ?? {});

    const fontFamilyChange = (data: F) => {
        const font_name = data.font_name;

        if (checkTeamStatusIsFree() && checkFontIdIsTeamFontId(data.id ?? 0)) {
            onClickVipBtn?.('font_expire');
            return;
        }
        setFontName(font_name);
        onChange?.(data, font_name, !!fontSearchText);
    };

    const handleFontGroupNameChange = (fontGroupName: string) => {
        const index = openGroups.indexOf(fontGroupName);
        const _openGroups = cloneDeep(openGroups);
        if (index === -1) {
            _openGroups.push(fontGroupName);
        } else {
            _openGroups.splice(index, 1);
        }
        setOpenGroups(_openGroups);
    };

    // 搜索字体 NotFound 结果
    const searchSelectNotFoundContent = (
        <div className="search-not_found" style={{ height: heightInfo.dropComponentHeight - 64 }}>
            <IconFont name="EmptyDrawer" fontSize={40} style={{ marginBottom: 6 }} />
            <div>{$t((v) => v.textFontFamily.empty)}</div>
            <div>{$t((v) => v.textFontFamily.changeSearchWord)}</div>
        </div>
    );

    const handleSearch = async (opts = { reset: false }) => {
        if (!fontSearchText) {
            setSearchedData(null);
            return;
        }

        if (opts.reset) {
            setSearchedData(null);
        }

        setIsEmptyShow(false);

        setLoading(true);
        const searchedData = await searchFontData({
            text,
            keyword: fontSearchText,
            allFontData: allFontData,
        });
        setSearchedData(searchedData);
        const isEmptyShow = !Object.keys(searchedData).length;
        setIsEmptyShow(isEmptyShow);
        setLoading(false);
    };

    const handleSearchCtrlor = useDebounceFn(handleSearch, {
        wait: searchDebounceDelay,
    });

    useUpdateEffect(() => {
        setRenderType(fontSearchText ? 'search' : 'all');

        if (fontSearchText) {
            handleSearchCtrlor.cancel();
            handleSearchCtrlor.run({ reset: true });
        } else {
            handleSearchCtrlor.cancel();
            handleSearch({ reset: true });
        }
    }, [fontSearchText]);

    useEffect(() => {
        if (renderType === 'all') {
            setIsEmptyShow(false);
        } else {
            setOpenGroups([]);
        }
    }, [renderType]);

    const handleTextItemEnter = (item: F) => {
        setVipBtnShow(item.is_vip === '1' && !isVip);
    };

    const handleClickFontItem = (item: F) => {
        fontFamilyChange(item);

        if (renderType === 'search') {
            window._stat_track?.('search_pop_click', {
                query: fontSearchText,
                class: '',
                page_id: 'zt_search_page',
            });
            window._stat_track?.('search_result_click', {
                query: fontSearchText,
                class: '',
                search_entrance: 'zt_search_page',
            });
        }
    };

    // 渲染选项
    const renderOption = ({ item, index, keyPrefix = 'other' }: { item: F; index: number; keyPrefix?: string }) => {
        return FontItemCom({
            item,
            index,
            keyPrefix,
            lazyLoadRenderKey,
            theme,
            freeUseVipFont,
            onClick: handleClickFontItem,
            onMouseEnter: handleTextItemEnter,
            onTrackShow,
        });
    };

    return (
        <div ref={setSelectDom}>
            <Select
                className={classNames('text-fontFamily', {
                    'text-fontFamily__open': open,
                })}
                optionLabelProp="label"
                optionFilterProp="label"
                placeholder="选择字体"
                value={fontName ?? undefined}
                dropdownMatchSelectWidth={228}
                popupClassName={classNames('text-fontFamily__dropdown', popupClassName)}
                dropdownStyle={{
                    zIndex: 11,
                    height: customHeightInfo?.dropComponentHeight ?? heightInfo.dropComponentHeight,
                }}
                virtual={false}
                suffixIcon={
                    <div className="text-fontFamily__icon" style={{ pointerEvents: 'none' }}>
                        <IconFont fontSize={14} name={open ? 'ChevronUpBlack' : 'ChevronDownBlack'} />
                    </div>
                }
                open={open}
                placement="bottomRight"
                notFoundContent={<></>}
                dropdownRender={(menu) => (
                    <div className="text-font-family-wrap" data-no-search={!searchEnable}>
                        {searchEnable && (
                            <div className="text-fontFamily__search">
                                <Input
                                    placeholder={$t((v) => v.textFontFamily.searchFont)}
                                    prefix={
                                        <IconFont
                                            fontSize={14}
                                            name="SearchBold"
                                            color="var(--content-system-primary)"
                                        />
                                    }
                                    suffix={
                                        fontSearchText ? (
                                            <IconFont
                                                fontSize={14}
                                                name="CrossCircleFill"
                                                onClick={() => setFontSearchText('')}
                                            />
                                        ) : null
                                    }
                                    value={fontSearchText}
                                    onChange={(e) => setFontSearchText(e.target.value)}
                                    onFocus={() => {
                                        window._stat_track?.('search_click', {
                                            page_id: 'zt_search_page',
                                        });
                                    }}
                                />
                            </div>
                        )}
                        {isEmptyShow ? (
                            searchSelectNotFoundContent
                        ) : (
                            <Scrollbars
                                style={{
                                    height: customHeightInfo?.dropListHeight ?? heightInfo.dropListHeight,
                                }}
                            >
                                {menu}
                            </Scrollbars>
                        )}
                        {vipBtnShow && !freeUseVipFont ? (
                            <div className="text-fontFamily__vip-btn" onClick={() => onClickVipBtn?.()}>
                                {$t((v) => v.textFontFamily.buyVipEnjoyFontFamily)}
                            </div>
                        ) : null}
                    </div>
                )}
                onDropdownVisibleChange={(open) => {
                    setOpen(open);
                    setTimeout(() => {
                        setLazyLoadRenderKey((k) => k + 1);
                    }, 300);
                    onDropdownVisibleChange?.(open);
                    if (open) {
                        refreshSelectDomRect();
                    }
                }}
            >
                {renderType === 'all' && usingFontList.length > 0 && (
                    <OptGroup key={'using'} label={$t((v) => v.textFontFamily.currentFontFamily)}>
                        {usingFontList.map((item, index) => renderOption({ item, index, keyPrefix: 'using' }))}
                    </OptGroup>
                )}

                {/* 全部列表面板 */}
                {renderType === 'all' &&
                    Object.entries(resultRenderData as RenderGroupData<F>).map(([languageKey, fontGroup]) => {
                        const firstGroupKey = Object.keys(fontGroup)[0];
                        const firstFontItem = fontGroup[firstGroupKey]?.[0];
                        const isTeamFont = checkFontIdIsTeamFontId(firstFontItem?.id ?? 0);
                        const showVip = isTeamFont && checkTeamStatusIsFree(teamInfo);
                        return (
                            <OptGroup
                                key={languageKey}
                                label={
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {languageKey}
                                        {showVip && <VipIcon style={{ marginLeft: 4 }} />}
                                    </div>
                                }
                            >
                                {Object.entries(fontGroup).map(([fontGroupName, fontItems]) => {
                                    //不是系列组的 直接返回Option
                                    if (fontGroupName === 'other' || fontGroupName === '品牌字体库') {
                                        return fontItems.map((item, index) => renderOption({ item, index }));
                                    }

                                    //系列组
                                    return FontGroupItem({
                                        fontGroupName,
                                        fontItems,
                                        fontName,
                                        lazyLoadRenderKey,
                                        openGroups,
                                        theme,
                                        onFontFamilyChange: handleClickFontItem,
                                        onFontGroupNameChange: handleFontGroupNameChange,
                                        onMouseEnter: handleTextItemEnter,
                                        onTrackShow,
                                    });
                                })}
                            </OptGroup>
                        );
                    })}

                {/* 搜索面板 */}
                {renderType === 'search' &&
                    Object.keys(resultRenderData).map((v) => {
                        const isTeamFont = checkFontIdIsTeamFontId((resultRenderData as RenderData<F>)[v][0].id ?? 0);
                        return (
                            <OptGroup
                                key={v}
                                label={
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {v}
                                        {isTeamFont && checkTeamStatusIsFree(teamInfo) && (
                                            <VipIcon style={{ marginLeft: 4 }} />
                                        )}
                                    </div>
                                }
                            >
                                {(resultRenderData as RenderData<F>)[v].map((item, index) =>
                                    renderOption({ item, index }),
                                )}
                            </OptGroup>
                        );
                    })}

                {loading && (
                    <div style={{ marginTop: 16, textAlign: 'center' }}>{$t((v) => v.infiniteScroll.loading)}</div>
                )}
            </Select>
        </div>
    );
};

export default TextFontFamily;

/** 用户侧, 与搜索相关的组件参数 */
export const UserBizSearchProps: Pick<ITextFontFamilyProps, 'searchFontData' | 'searchDebounceDelay'> = {
    searchDebounceDelay: 300,
    searchFontData: async ({ keyword, text = '' }) => {
        const PAGE_SIZE = 50;

        const ZH_CATEGORY_ID = 101;
        const CN_CATEGORY_ID = 102;
        const checkTextIsContainZH = (text: string) => /[\u4e00-\u9fa5]+/g.test(text);
        const checkTextIsContainCN = (text: string) => /[a-zA-Z]+/g.test(text);

        try {
            let data = await searchFonts({
                keyword: keyword.replace(/\s/g, ''),
                count: PAGE_SIZE,
                type: 'fonts',
            });
            const prioritizedCategoryId: number | null = checkTextIsContainZH(text)
                ? ZH_CATEGORY_ID
                : checkTextIsContainCN(text)
                  ? CN_CATEGORY_ID
                  : null;
            if (prioritizedCategoryId) {
                const teamFontCategoryItem = data.find((a) => a.category_id === 0);
                // 根据文本内容推断, 优先展示什么语言
                const otherCategories = data.sort((a, b) =>
                    a.category_id === prioritizedCategoryId ? -1 : b.category_id === prioritizedCategoryId ? 1 : 0,
                );
                data = teamFontCategoryItem ? [teamFontCategoryItem, ...otherCategories] : otherCategories;
            }
            const totalRenderData = apiData2renderData(data);
            window._stat_track?.('search_succeed', {
                query: keyword,
                class: '',
                result: Object.values(totalRenderData).length ? 1 : 0,
                page_id: 'zt_search_page',
            });
            return totalRenderData;
        } catch {
            return {};
        }

        function apiData2renderData(apiData: FontsCategoriesRaw): RenderData {
            const renderData: RenderData = {};
            apiData.forEach((categoryItem) => {
                renderData[categoryItem.category_name] = categoryItem.materials.map((material) => ({
                    // 分类id
                    category_id: material.category_id,
                    category_name: material.category_name,
                    id: material.id,
                    // 字体名称
                    font_name: material.font_name,
                    // 是否vip
                    is_vip: `${material.threshold}`,
                    material_id: material.id,
                    name: material.default_name,
                    thumbnail: material.thumbnail?.url,
                    thumbnail_text_dark: material.thumbnail_text_dark?.url,
                    thumbnail_text_white: material.thumbnail_text_white?.url,
                    zip_url: material.woff2_file?.url || material.font_file?.url, //原先定义的名字，后续统一用font_file就可以
                    font_file: material.woff2_file?.url || material.font_file?.url,
                    // 会员角标
                    corner_mark: material.corner_mark?.url,
                    type_id: material.type_id,
                    zip_file: material.zip_file.url,
                    font_group_name: material.font_group_name,
                    font_weight_value: material.font_weight_value,
                    font_group_thumbnail_dark: material.font_group_thumbnail_dark?.url || '',
                    font_group_thumbnail_white: material.font_group_thumbnail_white?.url || '',
                }));
            });
            return renderData;
        }
    },
};

const useDomRect = (domOrDomRef: React.RefObject<HTMLElement> | HTMLElement | null) => {
    const [rect, setRect] = useState<DOMRect | null>(null);

    const refreshRect = () => {
        const dom = domOrDomRef instanceof HTMLElement ? domOrDomRef : domOrDomRef?.current;
        if (!dom) return;
        setRect(dom.getBoundingClientRect());
    };

    useEffect(() => {
        const dom = domOrDomRef instanceof HTMLElement ? domOrDomRef : domOrDomRef?.current;
        if (!dom) return;

        setRect(dom.getBoundingClientRect());
        const observer = new MutationObserver((mutations) => {
            for (let mutation of mutations) {
                if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
                    try {
                        const rect = mutation.target.getBoundingClientRect();
                        setRect(rect);
                    } catch {
                        /* noop */
                    }
                }
            }
        });
        observer.observe(dom, {
            attributes: true,
        });
        return () => {
            observer.disconnect();
        };
    }, [domOrDomRef]);

    return {
        rect,
        refreshRect,
    };
};
